import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth, effectiveTier, planLabel } from "@/lib/AuthContext";
import DashboardShell from "@/components/dashboard/DashboardShell";
import api from "@/lib/api";
import { Video, Image as ImageIcon, Crown, ArrowRight } from "lucide-react";

export default function DashboardPage() {
  const { user, loading } = useAuth();
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    if (user) api.get("/billing/me/usage").then(({ data }) => setUsage(data)).catch(() => {});
  }, [user]);

  if (loading || user === undefined) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;

  const tier = effectiveTier(user);
  const isTrial = !tier;

  return (
    <DashboardShell>
      <div className="mb-12">
        <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#D4AF37] mb-4">
          Welcome back{user.name ? `, ${user.name.split(" ")[0]}` : ""}
        </p>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-[#F4F4F0] leading-[1.05]">
          What are we making today?
        </h1>
      </div>

      {/* Plan banner */}
      <div className="mb-12 border border-[#2A2A2A] bg-[#121212] p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6" data-testid="plan-status-card">
        <div className="flex items-center gap-5">
          <div className="w-14 h-14 border border-[#D4AF37]/40 flex items-center justify-center">
            <Crown className="w-6 h-6 text-[#D4AF37]" strokeWidth={1.5} />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#A0A0A0]">Plan Status</p>
            <p className="font-display text-2xl md:text-3xl font-extrabold text-[#F4F4F0] tracking-tight mt-1" data-testid="plan-status-label">
              {planLabel(user)}
            </p>
            {usage && (
              <p className="text-sm text-[#A0A0A0] mt-1" data-testid="downloads-remaining-label">
                <span className="text-[#D4AF37] font-bold">{Math.max(0, usage.downloads_limit - usage.downloads_used)}</span>{" "}
                of {usage.downloads_limit} downloads remaining
                {usage.window === "week" ? " this week" : usage.window === "month" ? " this month" : ""}
              </p>
            )}
            {usage?.subscription_cancelled && usage.subscription_expires_at && (
              <p className="text-xs text-red-300 mt-1">
                Cancelled — access ends {new Date(usage.subscription_expires_at).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>
        {isTrial && (
          <Link
            to="/pricing"
            data-testid="dashboard-upgrade-cta"
            className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#080808] font-bold px-6 py-3 hover:bg-[#F0C84B] transition-colors"
          >
            Upgrade Plan
            <ArrowRight className="w-4 h-4" />
          </Link>
        )}
        {!isTrial && (
          <Link
            to="/pricing"
            data-testid="dashboard-manage-cta"
            className="border border-[#2A2A2A] hover:border-[#D4AF37] px-6 py-3 transition-colors text-sm"
          >
            Manage Plan
          </Link>
        )}
      </div>

      {/* Generate options */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        <GenerateCard
          to="/generate/video"
          icon={Video}
          label="Generate Video"
          desc="Short commercial, promo, music or social clip. 4 / 8 / 12 second outputs in 9:16, 1:1, 16:9. Only downloaded videos count toward your plan."
          testId="card-generate-video"
        />
        <GenerateCard
          to="/generate/image"
          icon={ImageIcon}
          label="Generate Image"
          desc="High-quality static ad creatives, product shots, poster art. Generate freely, download what you keep."
          testId="card-generate-image"
        />
      </div>

      <div className="mt-10">
        <Link to="/renders" className="text-sm text-[#A0A0A0] hover:text-[#D4AF37] transition-colors" data-testid="dashboard-view-renders">
          → View all your renders
        </Link>
      </div>
    </DashboardShell>
  );
}

function GenerateCard({ to, icon: Icon, label, desc, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="group block border border-[#2A2A2A] hover:border-[#D4AF37] bg-[#0F0F0F] hover:bg-[#121212] p-8 md:p-10 transition-colors"
    >
      <Icon className="w-9 h-9 text-[#D4AF37] mb-8" strokeWidth={1.4} />
      <h3 className="font-display text-2xl md:text-3xl font-bold text-[#F4F4F0] tracking-tight mb-3">{label}</h3>
      <p className="text-[#A0A0A0] text-sm leading-relaxed mb-8">{desc}</p>
      <span className="inline-flex items-center gap-2 text-sm text-[#D4AF37] font-semibold">
        Start <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
      </span>
    </Link>
  );
}

function Loading() {
  return <div className="min-h-screen bg-[#080808] flex items-center justify-center text-[#A0A0A0]">Loading…</div>;
}
